import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useGetRresQuery } from "services/rre-api";
import { useSelector } from "utils/redux";
import type { OptionType } from "components/inputs";
import {
  Select,
  MultiSelect,
  DatePicker,
  DATE_TO_STRING,
  STRING_TO_DATE,
} from "components/inputs";
import { useConstants } from "utils/hooks";
import Button from "components/Button/Button.tsx";
import Card from "components/Card/Card";
import { useForm, Controller } from "react-hook-form";
import { useGenerateReportMutation } from "services/api";
import { useGetReportByIdMutation } from "services/reporting-api";
import { ReportTableDrawer } from "pages/reporting/ReportTableDrawer.tsx";
import { KaboodleToaster } from "components/common";
import "./Section111Reporting.scss";
import KaboodleModal from "components/Modals/KaboodleModal";
import { initiateDownload } from "components/common/DownloadLink.tsx";
import LoadingIcon from "components/Loading/LoadingIcon.tsx";

// Mirrors the backend reporting.constants.REPORT_TYPES values. Used only to pick
// which parameter fields render for the selected report type; the dropdown
// options themselves are sourced from the server (constants.REPORT_TYPES).
const REPORT_TYPES = {
  NEXT_SCHEDULED_ACTION: "NEXT_SCHEDULED_ACTION",
  QUERY_RESPONSE: "QUERY_RESPONSE",
  CLAIM_RESPONSE: "CLAIM_RESPONSE",
  UNSOLICITED_RESPONSE: "UNSOLICITED_RESPONSE",
  MISSING_REPORTING_FIELDS: "MISSING_REPORTING_FIELDS",
};

const MASK_INPUT_OPTIONS = [
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
];

export default function Section111Reporting() {
  const accessToken = useSelector((state) => state.main.accessToken);
  const { constants } = useConstants();
  const [reportId, setReportId] = useState(null);
  const [reportType, setReportType] = useState(null);
  const [generatingModalIsOpen, setGeneratingModalIsOpen] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      rre_ids: [],
      report_type: null,
      mask: "false",
      next_scheduled_action: [],
      response_received_gte: null,
      response_received_lte: null,
      response_date_gte: null,
      response_date_lte: null,
      is_medicare_beneficiary: null,
    },
  });

  const [generateReport] = useGenerateReportMutation();

  const { data: rres } = useGetRresQuery({ searchTerm: null });

  const rreOptions = useMemo(() => {
    return (
      rres?.results.map((rre) => ({ label: rre.name, value: rre.id })) || []
    );
  }, [rres]);

  const reportTypeOptions = useMemo(
    () => constants?.REPORT_TYPES ?? [],
    [constants],
  );

  const onSubmit = async (data) => {
    const response = await generateReport({
      data: {
        ...data,
        mask: data?.mask?.value,
        report_type: data?.report_type?.value,
        // RRE and Next Scheduled Action are multi-selects. Send the RRE ids as a
        // flat array; next_scheduled_action is forwarded as the array of option
        // objects (the backend normalizes both the option and raw-value shapes).
        rre_ids: data?.rre_ids?.map((option: OptionType) => option.value) ?? [],
      },
    });

    if (response.error || !(response as { data: { id: number } }).data?.id) {
      KaboodleToaster.error(`Failed to generate report.`);
      throw new Error("Failed to generate report");
    }

    setReportId((response as { data: { id: number } }).data.id);
    setGeneratingModalIsOpen(true);
  };
  const [getReportById] = useGetReportByIdMutation();

  const getReport = useCallback(
    async (reportId: number) => {
      try {
        const ret = await getReportById({
          reportId,
        }).unwrap();
        return ret;
      } catch (error) {
        KaboodleToaster.error(`Failed to get report.`);
        return null;
      }
    },
    [getReportById],
  );

  // Cancel flag + timer handle so an in-flight poll chain is torn down when the
  // modal closes or the component unmounts -- no setState / toast fires after
  // the generation flow has moved on.
  const pollCancelledRef = useRef(false);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pollReportStatus = useCallback(
    async (repId: number) => {
      const data = await getReport(repId);
      if (pollCancelledRef.current) return;
      pollTimeoutRef.current = setTimeout(() => {
        if (pollCancelledRef.current) return;
        try {
          const status = (data as { status: string })?.status;
          if (status === "COMPLETED") {
            initiateDownload(
              (data as { download_url: string }).download_url,
              accessToken,
              (data as { filename: string }).filename,
            );
            setGeneratingModalIsOpen(false);
          } else if (status === "FAILED") {
            setGeneratingModalIsOpen(false);
            KaboodleToaster.error(`Failed to generate report.`);
          } else if (status === "IN_PROGRESS" || status === "PENDING") {
            pollReportStatus(repId);
          } else {
            KaboodleToaster.error(`Failed to generate report.`);
          }
        } catch (error) {
          KaboodleToaster.error(`Failed to generate report.`);
        }
      }, 2000);
    },
    [getReport, accessToken],
  );

  // Drive polling from an effect (not a useMemo side-effect, which React may run
  // more than once) so it starts exactly once per report and is cancellable.
  useEffect(() => {
    if (!reportId || !generatingModalIsOpen) {
      return;
    }
    pollCancelledRef.current = false;
    pollReportStatus(reportId);
    return () => {
      pollCancelledRef.current = true;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, [reportId, generatingModalIsOpen, pollReportStatus]);

  return (
    <form>
      {reportId && generatingModalIsOpen && (
        <KaboodleModal
          isOpen={generatingModalIsOpen}
          onClose={() => {
            setGeneratingModalIsOpen(false);
          }}
        >
          <LoadingIcon message="Generating Report" />
        </KaboodleModal>
      )}
      <ReportTableDrawer />
      <div className="Section111Reporting">
        <Card heading="Reporting">
          <Controller
            name="rre_ids"
            control={control}
            rules={{
              validate: (value) =>
                (Array.isArray(value) && value.length > 0) ||
                "RREs are required",
            }}
            render={({ field: { name, onChange, value } }) => (
              <MultiSelect
                name={name}
                label="RREs"
                options={rreOptions}
                value={value}
                errors={errors}
                onChange={onChange}
                disableRemoveCondition={false}
                placeholder="Select RREs..."
              />
            )}
          />
          <Controller
            name="report_type"
            control={control}
            rules={{ required: "Report Type is required" }}
            render={({ field: { name, onChange } }) => (
              <Select
                name={name}
                label="Report Type"
                options={reportTypeOptions}
                errors={errors}
                onChange={(o) => {
                  onChange(o);
                  setReportType((o as OptionType).value);
                  // Reset the Next Scheduled Action selection when the report
                  // type changes so a stale selection from a prior NSA report
                  // isn't carried into the submitted payload.
                  setValue("next_scheduled_action", []);
                }}
              />
            )}
          />
          <Controller
            name="mask"
            control={control}
            rules={{ required: "Mask is required" }}
            render={({ field: { name, onChange, value } }) => (
              <Select
                name={name}
                label="Mask Input"
                value={MASK_INPUT_OPTIONS.find(
                  (option: OptionType) => option.value === value,
                )}
                options={MASK_INPUT_OPTIONS}
                errors={errors}
                onChange={onChange}
              />
            )}
          />
          {reportType === REPORT_TYPES.NEXT_SCHEDULED_ACTION && (
            <Controller
              name="next_scheduled_action"
              control={control}
              rules={{
                validate: (value) =>
                  (Array.isArray(value) && value.length > 0) ||
                  "Next Scheduled Action is required",
              }}
              render={({ field: { name, onChange, value } }) => (
                <MultiSelect
                  name={name}
                  label="Next Scheduled Action"
                  options={constants?.EXTENDED_NEXT_SCHEDULED_ACTIONS}
                  value={value}
                  errors={errors}
                  onChange={onChange}
                  disableRemoveCondition={false}
                  placeholder="Select Next Scheduled Actions..."
                />
              )}
            />
          )}
          {(reportType === REPORT_TYPES.QUERY_RESPONSE ||
            reportType === REPORT_TYPES.CLAIM_RESPONSE) && (
            <>
              <Controller
                name="response_received_gte"
                control={control}
                rules={{ required: "Response Date Start is required" }}
                render={({ field: { value, name, onChange } }) => (
                  <DatePicker
                    name={name}
                    initialDate={STRING_TO_DATE(value)}
                    label="Response Date Start"
                    onDateChange={(date) => onChange(DATE_TO_STRING(date))}
                    errors={errors}
                  />
                )}
              />
              <Controller
                name="response_received_lte"
                control={control}
                rules={{ required: "Response Date End is required" }}
                render={({ field: { value, name, onChange } }) => (
                  <DatePicker
                    name={name}
                    label="Response Date End"
                    initialDate={STRING_TO_DATE(value)}
                    onDateChange={(date) => onChange(DATE_TO_STRING(date))}
                    errors={errors}
                  />
                )}
              />
            </>
          )}
          {reportType === REPORT_TYPES.UNSOLICITED_RESPONSE && (
            <>
              <Controller
                name="response_date_gte"
                control={control}
                rules={{ required: "Response Date Start is required" }}
                render={({ field: { value, name, onChange } }) => (
                  <DatePicker
                    name={name}
                    initialDate={STRING_TO_DATE(value)}
                    label="Response Date Start"
                    onDateChange={(date) => onChange(DATE_TO_STRING(date))}
                    errors={errors}
                  />
                )}
              />
              <Controller
                name="response_date_lte"
                control={control}
                rules={{ required: "Response Date End is required" }}
                render={({ field: { value, name, onChange } }) => (
                  <DatePicker
                    name={name}
                    label="Response Date End"
                    initialDate={STRING_TO_DATE(value)}
                    onDateChange={(date) => onChange(DATE_TO_STRING(date))}
                    errors={errors}
                  />
                )}
              />
            </>
          )}
          {reportType === REPORT_TYPES.QUERY_RESPONSE && (
            <Controller
              name="is_medicare_beneficiary"
              control={control}
              render={({ field: { name, onChange } }) => (
                <Select
                  name={name}
                  label="Is Medicare Beneficiary?"
                  options={[
                    { label: "---", value: "none" },
                    { label: "True", value: "true" },
                    { label: "False", value: "false" },
                  ]}
                  onChange={(option) => {
                    onChange((option as OptionType).value);
                  }}
                  errors={errors}
                />
              )}
            />
          )}
          <Button onClick={handleSubmit(onSubmit)}>
            {isSubmitting ? "Generating..." : "Generate Report"}
          </Button>
        </Card>
      </div>
    </form>
  );
}
